import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SheepFormDialog = ({
  open,
  onOpenChange,
  selectedSheep,
  formData,
  setFormData,
  autoNumber,
  setAutoNumber,
  onFetchNextNumber,
  onSubmit,
  breeds,
  femelles,
  males,
  photoPreview,
  onFileChange,
  onDeletePhoto,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{selectedSheep ? 'Modifier le mouton' : 'Ajouter un mouton'}</DialogTitle>
        <DialogDescription>
          {selectedSheep ? 'Modifiez les informations du mouton' : 'Remplissez les informations du nouveau mouton'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="numero">Numero *</Label>
              {!selectedSheep && (
                <button
                  type="button"
                  onClick={() => {
                    const newAutoState = !autoNumber;
                    setAutoNumber(newAutoState);
                    if (newAutoState) onFetchNextNumber();
                  }}
                  className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                    autoNumber
                      ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {autoNumber ? 'Auto' : 'Manuel'}
                </button>
              )}
            </div>
            <Input
              id="numero"
              value={formData.numero}
              onChange={(e) => {
                setFormData({ ...formData, numero: e.target.value });
                setAutoNumber(false);
              }}
              required
              placeholder={autoNumber ? 'Numero genere automatiquement' : 'Saisissez le numero'}
              data-testid="sheep-numero-input"
              className={autoNumber ? 'bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700' : ''}
            />
            {autoNumber && !selectedSheep && (
              <p className="text-xs text-slate-600 dark:text-slate-400">
                Prochain numero: {formData.numero || 'Generation...'}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="nom">Nom</Label>
            <Input
              id="nom"
              value={formData.nom}
              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              data-testid="sheep-nom-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="date_naissance">Date de naissance *</Label>
            <Input
              id="date_naissance"
              type="date"
              value={formData.date_naissance}
              onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
              required
              data-testid="sheep-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="race">Race *</Label>
            <Select value={formData.race} onValueChange={(value) => setFormData({ ...formData, race: value })}>
              <SelectTrigger data-testid="sheep-race-select">
                <SelectValue placeholder="Selectionner une race" />
              </SelectTrigger>
              <SelectContent>
                {breeds.map((breed) => (
                  <SelectItem key={breed.id} value={breed.nom}>{breed.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="sexe">Sexe *</Label>
            <Select value={formData.sexe} onValueChange={(value) => setFormData({ ...formData, sexe: value })}>
              <SelectTrigger data-testid="sheep-sexe-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="femelle">Femelle</SelectItem>
                <SelectItem value="male">Male</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="poids">Poids (kg)</Label>
            <Input
              id="poids"
              type="number"
              step="0.1"
              min="0"
              value={formData.poids}
              onChange={(e) => setFormData({ ...formData, poids: e.target.value })}
              data-testid="sheep-poids-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="statut">Statut</Label>
          <Select value={formData.statut} onValueChange={(value) => setFormData({ ...formData, statut: value })}>
            <SelectTrigger data-testid="sheep-statut-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="actif">Actif</SelectItem>
              <SelectItem value="mort">Mort</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border rounded-lg p-4 bg-pink-50 dark:bg-pink-950">
          <h4 className="text-sm font-semibold text-pink-700 dark:text-pink-300 mb-3">Lignee (optionnel)</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mere_id">Mere</Label>
              <Select
                value={formData.mere_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, mere_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="sheep-mere-select">
                  <SelectValue placeholder="Selectionner la mere" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune / Inconnue</SelectItem>
                  {femelles.filter(s => s.id !== selectedSheep?.id).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      N {s.numero} {s.nom && `- ${s.nom}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pere_id">Pere</Label>
              <Select
                value={formData.pere_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, pere_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="sheep-pere-select">
                  <SelectValue placeholder="Selectionner le pere" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun / Inconnu</SelectItem>
                  {males.filter(s => s.id !== selectedSheep?.id).map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      N {s.numero} {s.nom && `- ${s.nom}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-3">
            <Label htmlFor="origine">Origine</Label>
            <Select
              value={formData.origine || "none"}
              onValueChange={(value) => setFormData({ ...formData, origine: value === "none" ? "" : value })}
            >
              <SelectTrigger data-testid="sheep-origine-select">
                <SelectValue placeholder="Selectionner l'origine" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Non specifiee</SelectItem>
                <SelectItem value="naissance">Naissance a la ferme</SelectItem>
                <SelectItem value="achat">Achat</SelectItem>
                <SelectItem value="don">Don</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="photo">Photo du mouton</Label>
          {selectedSheep?.photo_url && !photoPreview && (
            <div className="mt-2 flex items-start gap-3">
              <img
                src={`${BACKEND_URL}${selectedSheep.photo_url}`}
                alt="Photo actuelle"
                className="w-32 h-32 object-cover rounded-lg border"
              />
              <Button type="button" variant="destructive" size="sm" onClick={onDeletePhoto} data-testid="delete-photo-button">
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer
              </Button>
            </div>
          )}
          <Input id="photo" type="file" accept="image/*" onChange={onFileChange} data-testid="sheep-photo-input" />
          {photoPreview && (
            <div className="mt-2">
              <img src={photoPreview} alt="Apercu" className="w-32 h-32 object-cover rounded-lg" />
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            data-testid="sheep-notes-input"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            className="flex-1 bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
            data-testid="sheep-submit-button"
          >
            {selectedSheep ? 'Modifier le mouton' : 'Ajouter le mouton'}
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
